/**
 * @author Madhuri <sai.madhuri@314ecorp.com>
 * @description Edit enrollment Status
 */
import _ from 'lodash';
import { Button, Dropdown, Space, Tooltip, App } from 'antd';
import { Down } from '@icon-park/react';

import { useAppStoreValues } from 'store/app';
import useUpdateEnrollmentData, { IParams } from 'hooks/admin/mutate/useUpdateEnrollmentData';
import {
	ELIGIBILITY_PAYER_ENROLLMENT_STATUS,
	ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES,
	ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS,
} from 'app-constants/eligibility';
import { MODAL_TYPES } from 'components/widgets/modal';
import { NO_ACCESS_TOOLTIP_TEXT } from '@veritable/common/constants';

/**
 * interface for props
 */
export interface IProps {
	payer?: Payers.IRegisteredPayer;
	providerDetails: Provider.IProviderEnrollmentTableData;
	npiMeta?: Metadata.IProviderMetaObj;
}

const ChangeEnrollmentStatus: React.FC<IProps> = ({ payer, providerDetails, npiMeta }) => {
	const { modal } = App.useApp();
	const { userAccess } = useAppStoreValues();
	const { mutateAsync } = useUpdateEnrollmentData();

	const enableDropdown = _.find(npiMeta, (meta) => meta.entity.id === providerDetails.providerId)?.isEkycCompleted;

	const getModalContent = (enrollmentStatus: Payers.IEnrollmentStatus): string => {
		if (enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.completed) {
			return 'You are proceeding to mark the provider as "Enrolled".';
		} else if (enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.failed) {
			return 'You are proceeding to mark the provider enrollment as "Rejected".';
		} else if (enrollmentStatus === ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.pending) {
			return 'You are proceeding to request re-enrollment for the provider.';
		} else {
			return `You are proceeding to request enrollment for ${payer?.name}.`;
		}
	};

	const getApiParams = (newStatus: Payers.IEnrollmentStatus) => {
		const apiParams: IParams = { payerID: payer?.id };
		const updateModel = [
			{
				providerId: providerDetails?.providerId,
				enrollmentStatus: newStatus,
			},
		];

		// internal admin approve / reject enrollment
		if (userAccess.isInternalAdmin) {
			apiParams.adminModel = updateModel;
		}
		// customer admin / user request enrollment
		else {
			apiParams.userModel = updateModel;
		}
		return apiParams;
	};

	// to handle enroll and re-enroll
	const showConfirmModal = (newStatus: Payers.IEnrollmentStatus) => {
		if (payer?.id && newStatus !== providerDetails.enrollmentStatus) {
			const apiParams = getApiParams(newStatus);
			modal.confirm({
				...MODAL_TYPES.warning,
				width: 500,
				okText: 'Yes, Proceed',
				content: getModalContent(newStatus),
				onOk: () => mutateAsync(apiParams),
			});
		}
	};

	const tooltipText = enableDropdown ? '' : 'Please contact the provider to complete their electronic verification';

	// if internal admin
	if (userAccess?.isInternalAdmin) {
		const { label, color } = _.get(ELIGIBILITY_PAYER_ENROLLMENT_STATUS, [providerDetails?.enrollmentStatus as number]) || {};
		return (
			<Dropdown
				trigger={['click']}
				disabled={!enableDropdown || !userAccess.canUpdatePayers}
				menu={{
					items: [
						{
							key: 'enrolled',
							label: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.completed.action,
							onClick: () => showConfirmModal(ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.completed),
						},
						{
							key: 'failed-enrollment',
							label: ELIGIBILITY_PAYER_ENROLLMENT_TAG_STATUS.failed.action,
							onClick: () => showConfirmModal(ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.failed),
						},
					],
				}}
			>
				<Tooltip title={userAccess.canUpdatePayers ? tooltipText : NO_ACCESS_TOOLTIP_TEXT}>
					<Button
						disabled={!enableDropdown || !userAccess.canUpdatePayers}
						className='w-[170px]'
						style={{ color, borderColor: color }}
					>
						<Space>
							{label}
							<Down />
						</Space>
					</Button>
				</Tooltip>
			</Dropdown>
		);
	}
};

export default ChangeEnrollmentStatus;
